import React, { useState, useEffect, useMemo } from "react";
import { getData } from "../../../CommonElements/Functions/PostGetFun";
import { API } from "../../../api/API";
import "../Styles/leaves.css";
import ApplyLeaveModal from "./ApplyLeaveModal";

interface LeaveEvent {
  dateStr: string;
  type: "Approved Leave" | "Raised Leave" | "Holiday" | "Week Off";
}

interface LeaveBalanceItem {
  key: string;
  resolvedLabel: string;
  Opening: number;
  Entitlement: number;
  Consumed: number;
  Balance: number;
  InProgress?: number;
}

const defaultLeaveTypeLabels: Record<string, string> = {
  CL: "Casual Leave",
  PL: "Privilege Leave",
  ML: "Medical Leave",
  MATL: "Maternity Leave",
  EL: "Election Leave",
  LWP: "Leave Without Pay",
  FTL: "Floating Leave",
  MLDL: "Marriage Leave",
  LL: "Loyalty Leave",
  UL: "Unpaid Leave",
  SL: "Sick Leave",
};

const Leaves: React.FC<{ employeeIdProp?: number }> = ({ employeeIdProp }) => {
  const activeEmpId =
    employeeIdProp ||
    Number(localStorage.getItem("empId")) ||
    Number(localStorage.getItem("employeeId")) ||
    Number(localStorage.getItem("userId")) ||
    0;

  // Active viewing month/year for the calendar
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  // User selected specific date (for clicking on calendar days)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Leave calendar events and details
  const [events, setEvents] = useState<LeaveEvent[]>([]);
  const [leaveDetails, setLeaveDetails] = useState<any[]>([]);
  const [leaveBalance, setLeaveBalance] = useState<any>(null);

  // Modals state
  const [isApplyLeaveOpen, setIsApplyLeaveOpen] = useState<boolean>(false);
  const [selectedViewLeaveId, setSelectedViewLeaveId] = useState<string | number | undefined>(undefined);
  const [isViewLeaveOpen, setIsViewLeaveOpen] = useState<boolean>(false);

  // Calculate first and last dates for the query
  const calculateMonthRange = (date: Date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDayOfYear = new Date(date.getFullYear(), 11, 31);

    const formatDate = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      fromDate: formatDate(firstDay),
      toDate: formatDate(lastDayOfYear),
    };
  };

  // Utility to get dates array between two dates
  const getDatesBetweenTwoDates = (startDate: string, endDate: string): string[] => {
    const start = new Date(startDate.split("T")[0]);
    const end = new Date(endDate.split("T")[0]);
    const dates: string[] = [];

    const cur = new Date(start);
    while (cur <= end) {
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, "0");
      const d = String(cur.getDate()).padStart(2, "0");
      dates.push(`${y}-${m}-${d}`);
      cur.setDate(cur.getDate() + 1);
    }
    return dates;
  };

  // Fetch employee leave data
  const fetchEmployeeLeave = async (date: Date) => {
    if (!activeEmpId) return;
    const serverUrl = localStorage.getItem("serverUrl");
    const url = `${serverUrl}${API.getEmployeeLeave}`;
    const { fromDate, toDate } = calculateMonthRange(date);

    try {
      const { data, status } = await getData(url, true, {
        employee_id: activeEmpId,
        from_date: fromDate,
        to_date: toDate,
      });

      if (status === 200 && data?.data) {
        const leaveEvents: LeaveEvent[] = [];
        const leaveDetailsData: any[] = [];

        // Process ApprovedLeave
        (data.data.ApprovedLeave || []).forEach((leaveDate: string) => {
          leaveEvents.push({
            dateStr: leaveDate.split("T")[0],
            type: "Approved Leave",
          });
        });

        // Process Holiday
        (data.data.Holiday || []).forEach((holidayDate: string) => {
          leaveEvents.push({
            dateStr: holidayDate.split("T")[0],
            type: "Holiday",
          });
        });

        // Process WeekOff
        (data.data.WeekOff || []).forEach((weekOffDate: string) => {
          leaveEvents.push({
            dateStr: weekOffDate.split("T")[0],
            type: "Week Off",
          });
        });

        // Process LeaveRequest
        if (data.data.LeaveRequest) {
          leaveDetailsData.push(...data.data.LeaveRequest);
          data.data.LeaveRequest.forEach(
            (leave: {
              LeaveFrom: string;
              LeaveTo: string;
              LeaveStatus: number;
            }) => {
              const leaveDates = getDatesBetweenTwoDates(
                leave.LeaveFrom,
                leave.LeaveTo
              );
              const leaveType: "Approved Leave" | "Raised Leave" =
                leave.LeaveStatus === 2 ? "Approved Leave" : "Raised Leave";
              leaveDates.forEach((dStr: string) => {
                leaveEvents.push({ dateStr: dStr, type: leaveType });
              });
            }
          );
        }

        setEvents(leaveEvents);
        setLeaveDetails(leaveDetailsData);
      }
    } catch (error) {
      console.error("Error fetching employee leave data:", error);
    }
  };

  // Fetch leave balance data
  const fetchLeaveBalance = async (year: string) => {
    if (!activeEmpId) return;
    const serverUrl = localStorage.getItem("serverUrl");
    const url = `${serverUrl}${API.getEmployeeLeaveBalance}`;

    try {
      const { data, status } = await getData(url, true, {
        employee_id: activeEmpId,
        year,
      });

      if (status === 200 && data?.data) {
        setLeaveBalance(data?.data);
      }
    } catch (error) {
      console.error("Error fetching leave balance data:", error);
    }
  };

  useEffect(() => {
    fetchEmployeeLeave(currentDate);
    fetchLeaveBalance(currentDate.getFullYear().toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEmpId]);

  // Handle Month Navigation
  const handlePrevMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    setCurrentDate(newDate);
    fetchEmployeeLeave(newDate);
    if (newDate.getFullYear() !== currentDate.getFullYear()) {
      fetchLeaveBalance(newDate.getFullYear().toString());
    }
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    setCurrentDate(newDate);
    fetchEmployeeLeave(newDate);
    if (newDate.getFullYear() !== currentDate.getFullYear()) {
      fetchLeaveBalance(newDate.getFullYear().toString());
    }
  };

  // Prepare Leave Balance Cards Array
  const balanceList: LeaveBalanceItem[] = useMemo(() => {
    if (leaveBalance && typeof leaveBalance === "object" && Object.keys(leaveBalance).length > 0) {
      return Object.keys(leaveBalance).map((key) => {
        const item = leaveBalance[key];
        const rawType = String(item.LeaveType || item.type || key).trim();
        const upperCode = rawType.toUpperCase();
        const resolvedLabel = defaultLeaveTypeLabels[upperCode] || rawType || key;

        return {
          key,
          resolvedLabel,
          Opening: Number(item.Opening ?? 0),
          Entitlement: Number(item.Entitlement ?? item.Accuration ?? item.Entitled ?? 0),
          Consumed: Number(item.Consumed ?? 0),
          Balance: Number(item.Balance ?? item.Available ?? 0),
          InProgress: Number(item.InProgress ?? 0),
        };
      });
    }

    // Default matching Screenshot 1 if no balance records yet
    return [
      { key: "ML", resolvedLabel: "Medical Leave", Opening: 0, Entitlement: 0, Consumed: 0, Balance: 0 },
      { key: "MATL", resolvedLabel: "Maternity Leave", Opening: 0, Entitlement: 0, Consumed: 0, Balance: 0 },
      { key: "EL", resolvedLabel: "Election Leave", Opening: 0, Entitlement: 0, Consumed: 0, Balance: 0 },
      { key: "CL", resolvedLabel: "Casual Leave", Opening: 0, Entitlement: 0, Consumed: 0, Balance: 0 },
    ];
  }, [leaveBalance]);

  // Calendar Day Generation
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth(); // 0-indexed
  const firstDayOfWeek = new Date(year, month, 1).getDay(); // 0 = Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const leadingBlanks = Array.from({ length: firstDayOfWeek }, (_, i) => null);
  const monthDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const calendarCells = [...leadingBlanks, ...monthDays];

  // Helper to format dates like "14 September 2026"
  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return "";
    const cleanStr = dateStr.split("T")[0];
    const parts = cleanStr.split("-");
    if (parts.length === 3) {
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      const day = String(d.getDate()).padStart(2, "0");
      const mName = d.toLocaleDateString("en-US", { month: "long" });
      const yNum = d.getFullYear();
      return `${day} ${mName} ${yNum}`;
    }
    return dateStr;
  };

  // Helper to check if a leave request matches selected date or month
  const filteredLeaveDetails = useMemo(() => {
    if (!selectedDate) {
      // By default show all leave details in this month or fetched
      return leaveDetails;
    }
    const selYear = selectedDate.getFullYear();
    const selMonth = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const selDay = String(selectedDate.getDate()).padStart(2, "0");
    const selDateStr = `${selYear}-${selMonth}-${selDay}`;

    return leaveDetails.filter((item) => {
      const from = item.LeaveFrom?.split("T")[0];
      const to = item.LeaveTo?.split("T")[0];
      if (from && to) {
        return selDateStr >= from && selDateStr <= to;
      }
      return false;
    });
  }, [leaveDetails, selectedDate]);

  return (
    <div className="leaves-page-container">
      {/* =========================================
          TOP SECTION: LEAVE BALANCE CARDS ROW
          ========================================= */}
      <div className="leave-balance-panel">
        <div className="leave-balance-row">
          {balanceList.map((item) => {
            const availVal = Math.max(0, item.Balance);
            const availStr = String(availVal).padStart(2, "0");

            return (
              <div key={item.key} className="leave-balance-item">
                <div className="leave-balance-info">
                  <div className="leave-balance-name">{item.resolvedLabel}</div>
                  <div className="leave-balance-stat">
                    Opening: <span>{item.Opening}</span>
                  </div>
                  <div className="leave-balance-stat">
                    Entitlement: <span>{item.Entitlement}</span>
                  </div>
                  <div className="leave-balance-stat">
                    Consumed: <span>{item.Consumed}</span>
                  </div>
                </div>

                <div className="leave-balance-circle-wrap">
                  <div className="leave-balance-circle">
                    <span className="leave-balance-num">{availStr}</span>
                    <span className="leave-balance-avail">Available</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* =========================================
          BOTTOM SECTION: CALENDAR (LEFT) & LEAVE REQUESTS (RIGHT)
          ========================================= */}
      <div className="leaves-content-split">
        {/* Left Column: Calendar and Legend */}
        <div className="calendar-section-wrap">
          <div className="calendar-card">
            {/* Navigation Header */}
            <div className="calendar-header-nav">
              <button
                type="button"
                className="cal-nav-btn"
                onClick={handlePrevMonth}
                aria-label="Previous Month"
              >
                <i className="fa fa-chevron-left"></i>
              </button>
              <div className="cal-nav-title">
                {currentDate.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </div>
              <button
                type="button"
                className="cal-nav-btn"
                onClick={handleNextMonth}
                aria-label="Next Month"
              >
                <i className="fa fa-chevron-right"></i>
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="calendar-grid">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((dayName, idx) => (
                <div
                  key={dayName}
                  className={`cal-weekday ${idx === 0 ? "sunday" : ""}`}
                >
                  {dayName}
                </div>
              ))}

              {calendarCells.map((dayNum, index) => {
                if (dayNum === null) {
                  return <div key={`blank-${index}`} className="cal-cell empty"></div>;
                }

                const dayDateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
                const dayOfWeek = (firstDayOfWeek + (dayNum - 1)) % 7;
                const isSunday = dayOfWeek === 0;

                // Check event statuses for this date
                const dayEvents = events.filter((e) => e.dateStr === dayDateStr);
                const isPending = dayEvents.some((e) => e.type === "Raised Leave");
                const isApproved = dayEvents.some((e) => e.type === "Approved Leave");
                const isHoliday = dayEvents.some((e) => e.type === "Holiday");
                const isWeekOff = dayEvents.some((e) => e.type === "Week Off");

                // Check today
                const now = new Date();
                const isToday =
                  now.getFullYear() === year &&
                  now.getMonth() === month &&
                  now.getDate() === dayNum;

                // Check selected
                const isSelected =
                  selectedDate &&
                  selectedDate.getFullYear() === year &&
                  selectedDate.getMonth() === month &&
                  selectedDate.getDate() === dayNum;

                let eventClass = "";
                if (isPending) eventClass = "event-pending";
                else if (isApproved) eventClass = "event-approved";
                else if (isHoliday) eventClass = "event-holiday";
                else if (isWeekOff) eventClass = "event-weekoff";
                else if (isToday) eventClass = "today-active";

                return (
                  <div
                    key={`day-${dayNum}`}
                    className="cal-cell"
                    onClick={() => {
                      if (isSelected) {
                        setSelectedDate(null);
                      } else {
                        setSelectedDate(new Date(year, month, dayNum));
                      }
                    }}
                  >
                    <div
                      className={`cal-date-number ${eventClass} ${
                        isSunday && !eventClass ? "sunday" : ""
                      } ${isSelected ? "selected-day" : ""}`}
                    >
                      {dayNum}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Calendar Legend matching Screenshot 1 */}
            <div className="calendar-legend-row">
              <div className="legend-item">
                <span className="legend-dot approved"></span>
                <span>Approved</span>
              </div>
              <div className="legend-item">
                <span className="legend-dot pending"></span>
                <span>Pending</span>
              </div>
              <div className="legend-item">
                <span className="legend-dot holidays"></span>
                <span>Holidays</span>
              </div>
              <div className="legend-item">
                <span className="legend-dot weekoff"></span>
                <span>Week Off</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Leave Request List & Apply Leave */}
        <div className="leave-requests-section">
          <div className="leave-requests-header">
            <h4 className="leave-requests-title">Leave Request</h4>
            <button
              type="button"
              className="apply-leave-btn"
              onClick={() => setIsApplyLeaveOpen(true)}
            >
              Apply Leave
            </button>
          </div>

          {selectedDate && (
            <div className="leave-filter-bar">
              <span>
                Showing requests for:{" "}
                <strong>
                  {selectedDate.toLocaleDateString("en-US", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </strong>
              </span>
              <button
                type="button"
                className="leave-filter-clear"
                onClick={() => setSelectedDate(null)}
              >
                Clear filter (Show all)
              </button>
            </div>
          )}

          {filteredLeaveDetails.length > 0 ? (
            filteredLeaveDetails.map((leave, idx) => {
              const daysCount = leave.LeaveDays || 1;
              const daysLabel = `${daysCount} Full day${daysCount > 1 ? "s" : ""}`;
              const statusClass =
                leave.LeaveStatus === 2
                  ? "approved"
                  : leave.LeaveStatus === 3
                  ? "rejected"
                  : "raised";
              const statusText =
                leave.LeaveStatus === 2
                  ? "Approved"
                  : leave.LeaveStatus === 3
                  ? "Rejected"
                  : "Raised";

              return (
                <div key={leave.LeaveReqId || idx} className="leave-req-card">
                  <div className="leave-req-row">
                    <span className="leave-req-type">
                      {leave.LeaveType || "Leave Without Pay"}
                    </span>
                    <span className="leave-req-days">{daysLabel}</span>
                  </div>

                  <div className="leave-req-row">
                    <span className="leave-req-dates">
                      {formatDisplayDate(leave.LeaveFrom)} To{" "}
                      {formatDisplayDate(leave.LeaveTo)}
                    </span>
                    <span className={`leave-status-pill ${statusClass}`}>
                      {statusText}
                    </span>
                  </div>

                  <div className="leave-req-reason">
                    {leave.LeaveReason || "No reason provided"}
                  </div>

                  <div className="leave-req-manager">
                    Manager:
                    {leave.LeaveRejectRemark && (
                      <span className="leave-req-manager-text">
                        {leave.LeaveRejectRemark}
                      </span>
                    )}
                  </div>

                  {leave.Attachment && (
                    <div>
                      <button
                        type="button"
                        className="btn-view-attachment"
                        onClick={() => {
                          setSelectedViewLeaveId(leave.LeaveReqId);
                          setIsViewLeaveOpen(true);
                        }}
                      >
                        View Attachment
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="no-leaves-placeholder">
              No leave requests found {selectedDate ? "for this date" : "for this period"}.
            </div>
          )}
        </div>
      </div>

      {/* Apply Leave Modal */}
      <ApplyLeaveModal
        isOpen={isApplyLeaveOpen}
        toggle={() => setIsApplyLeaveOpen(!isApplyLeaveOpen)}
        empId={activeEmpId}
        status="Apply"
        onSuccess={() => {
          fetchEmployeeLeave(currentDate);
          fetchLeaveBalance(currentDate.getFullYear().toString());
        }}
      />

      {/* View Attachment / Details Modal */}
      <ApplyLeaveModal
        isOpen={isViewLeaveOpen}
        toggle={() => setIsViewLeaveOpen(!isViewLeaveOpen)}
        leaveRequestId={selectedViewLeaveId}
        status="View"
        autoOpenMedia={true}
      />
    </div>
  );
};

export default Leaves;
